import { formatNumber } from "../../utils/useFormatNumber";

export function transformOverviewStats(queueStats, jobStats) {
  const totalSuccessful = queueStats.reduce((sum, q) => sum + q.totalSuccessful, 0);
  const numberOfQueues = queueStats.length;
  const avgRuntimeQues = formatNumber(
    queueStats.reduce((sum, q) => sum + q.averageRuntime, 0) / numberOfQueues || 0
  );
  const numberOfJobs = jobStats.length;
  const avgRuntimeJobs = formatNumber(
    jobStats.reduce((sum, j) => sum + j.averageRuntime, 0) / numberOfJobs || 0
  );

  const result = [
    { label: 'Average completion time of Queues', value: avgRuntimeQues },
    { label: 'Average completion time of Jobs', value: avgRuntimeJobs },
    { label: 'Process count', value: numberOfQueues },
    { label: 'Processes run', value: formatNumber(totalSuccessful) },
  ];

  return result;
}

export function transformTasksByStatus(queueStats) {
  const result = statesWrapper(queueStats);
  return result;
}

export function transformAverageQueueTime(queueStats) {
  const pairs = queueStats
    .map((q) => ({
      name: q.queueDefinitionName,
      runtime: q.averageRuntime,
    }))
    .sort((a, b) => b.runtime - a.runtime);

  const categories = pairs.map((p) => p.name);
  const seriesData = pairs.map((p) => p.runtime);

  return {
    series: [
      {
        name: 'Average Queue Time (seconds)',
        data: seriesData,
      },
    ],
    categories,
  };
}

export function transformTasksPerMonth(queueStats) {
  const monthlyTotals = Array(12).fill(0);
  queueStats.forEach((queue) => {
    queue.monthlyStats.forEach((stat) => {
      const month = stat.month - 1;
      monthlyTotals[month] += stat.totalQueueItems || 0;
    });
  });

  const lastMonth = monthlyTotals.findLastIndex(val => val > 0) + 1;

  const monthLabels = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ];

  const categories = monthLabels.slice(0, lastMonth);
  const data = monthlyTotals.slice(0, lastMonth).map(formatNumber);

  return {
    series: [{ name: 'Total Tasks', data }],
    xaxis: { categories },
  };
}

export function transformJobsByState(jobStats) {
  return statesWrapper(jobStats);
}

function statesWrapper(stats) {
  const totalSuccessful = stats.reduce((sum, stat) => sum + (stat.totalSuccessful || 0), 0);
  const totalFailed = stats.reduce((sum, stat) => sum + (stat.totalFailed || 0), 0);
  const totalStopped = stats.reduce((sum, stat) => sum + (stat.totalStopped || 0), 0);

  const pieData = [
    { name: 'Successful', value: formatNumber(totalSuccessful) },
    { name: 'Failed', value: formatNumber(totalFailed) },
    { name: 'Stopped', value: formatNumber(totalStopped) },
  ];

  return pieData.filter(item => item.value > 0);
}

export function transformAvgJobRuntime(jobStats) {
  const pairs = jobStats
    .map((job) => ({
      name: job.releaseName,
      runtime: job.averageRuntime,
    }))
    .sort((a, b) => b.runtime - a.runtime);

  const categories = pairs.map((p) => p.name);
  const data = pairs.map((p) => p.runtime);

  return {
    series: [
      {
        name: 'Average Runtime (seconds)',
        data,
      },
    ],
    categories,
  };
}

export function transformProcessesByMonthMapped(processes) {
  const counts = {};
  processes.forEach((proc) => {
    const date = new Date(proc.CreationTime.original);
    const label = date.toLocaleString('default', { month: 'short', year: 'numeric' });
    counts[label] = (counts[label] || 0) + 1;
  });

  const sortedMonths = Object.keys(counts).sort(
    (a, b) => new Date(a + ' 1') - new Date(b + ' 1')
  );

  const data = sortedMonths.map((m) => formatNumber(counts[m]));

  return {
    series: [{ name: 'Processes Released', data }],
    xaxis: { categories: sortedMonths },
  };
}

export function transformJobStatusPerMonth(jobStats) {
  const successful = Array(12).fill(0);
  const failed = Array(12).fill(0);
  const stopped = Array(12).fill(0);

  jobStats.forEach((job) => {
    job.monthlyStats.forEach((stat) => {
      const month = stat.month - 1;
      successful[month] += stat.successfulJobItems || 0;
      failed[month] += stat.failedJobItems || 0;
      stopped[month] += stat.stoppedJobItems || 0;
    });
  });

  const lastMonth = Math.max(
    ...jobStats.flatMap((j) => j.monthlyStats.map((stat) => stat.month)),
    0
  );

  const year = jobStats[0]?.monthlyStats[0]?.year || new Date().getFullYear();
  const monthDates = Array.from({ length: 12 }, (_, i) =>
    new Date(year, i, 1).toISOString().split('T')[0]
  );

  const categories = monthDates.slice(0, lastMonth);
  const dataSuccessful = successful.slice(0, lastMonth).map(formatNumber);
  const dataFailed = failed.slice(0, lastMonth).map(formatNumber);
  const dataStopped = stopped.slice(0, lastMonth).map(formatNumber)

  return {
    series: [
      { name: 'Successful', data: dataSuccessful },
      { name: 'Failed', data: dataFailed },
      { name: 'Stopped', data: dataStopped}
    ],
    categories,
  };
}